import { useState, useEffect, useCallback } from "react";
import { StickyNote, Copy, Trash2, Search, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const MAX_SNIPPETS = 50;

function getStorageKey(userId) {
  return `crm_snippets_${userId || "default"}`;
}

function loadSnippets(userId) {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSnippets(userId, snippets) {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(snippets));
  } catch {}
}

export default function SnippetsPanel({ userId }) {
  const [snippets, setSnippets] = useState(() => loadSnippets(userId));
  const [newText, setNewText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Persist on change
  useEffect(() => {
    saveSnippets(userId, snippets);
  }, [userId, snippets]);

  const addSnippet = useCallback(() => {
    const text = newText.trim();
    if (!text) return;

    const snippet = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      text,
      createdAt: new Date().toISOString(),
    };

    setSnippets((prev) => {
      const updated = [snippet, ...prev];
      // Rotate: keep only MAX_SNIPPETS
      return updated.slice(0, MAX_SNIPPETS);
    });
    setNewText("");
    toast.success("Nota guardada");
  }, [newText]);

  const deleteSnippet = useCallback((id) => {
    setSnippets((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const copySnippet = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copiado al portapapeles");
    }).catch(() => {
      toast.error("No se pudo copiar");
    });
  }, []);

  const filteredSnippets = searchQuery
    ? snippets.filter((s) => s.text.toLowerCase().includes(searchQuery.toLowerCase()))
    : snippets;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button className="relative p-1.5 rounded-md hover:bg-cyan-400/10 transition-colors group">
          <StickyNote className="w-4 h-4 theme-text-muted group-hover:text-cyan-400 transition-colors" />
          {snippets.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-cyan-500 text-[8px] text-white rounded-full flex items-center justify-center font-bold">
              {snippets.length > 9 ? "9+" : snippets.length}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent side="left" className="w-80 sm:w-96 theme-bg-secondary p-0 flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-3" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <SheetTitle className="theme-text flex items-center gap-2 text-base">
            <StickyNote className="w-4 h-4 text-cyan-400" />
            Notas rapidas
            <Badge variant="secondary" className="text-[10px] ml-auto mr-6">
              {snippets.length}/{MAX_SNIPPETS}
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {/* New snippet input */}
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <div className="flex gap-2">
            <textarea
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  addSnippet();
                }
              }}
              placeholder="Escribir nota... (Ctrl+Enter para guardar)"
              rows={2}
              className="flex-1 px-3 py-2 rounded-md text-xs theme-bg theme-text resize-none focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
              style={{ border: '1px solid var(--theme-border)' }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-[10px] theme-text-muted">{newText.length} caracteres</span>
            <Button size="sm" onClick={addSnippet} disabled={!newText.trim()} className="h-7 text-xs">
              <Plus className="w-3 h-3 mr-1" />
              Guardar
            </Button>
          </div>
        </div>

        {/* Search */}
        {snippets.length > 3 && (
          <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--theme-border)' }}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 theme-text-muted" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar notas..."
                className="w-full pl-8 pr-8 py-1.5 rounded-md text-xs theme-bg theme-text focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ border: '1px solid var(--theme-border)' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 theme-text-muted hover:theme-text"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Snippets list */}
        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
          {filteredSnippets.length === 0 ? (
            <div className="text-center py-8">
              <StickyNote className="w-8 h-8 mx-auto mb-2 theme-text-muted opacity-30" />
              <p className="text-xs theme-text-muted">
                {searchQuery ? "Sin resultados" : "Sin notas guardadas"}
              </p>
            </div>
          ) : (
            filteredSnippets.map((snippet) => (
              <div
                key={snippet.id}
                className="group rounded-lg px-3 py-2.5 theme-bg transition-colors hover:bg-cyan-400/5"
                style={{ border: '1px solid var(--theme-border)' }}
              >
                <p className="text-xs theme-text whitespace-pre-wrap break-words leading-relaxed">
                  {snippet.text}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] theme-text-muted">
                    {new Date(snippet.createdAt).toLocaleString("es-ES", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => copySnippet(snippet.text)}
                      className="p-1 rounded hover:bg-cyan-400/10 theme-text-muted hover:text-cyan-400 transition-colors"
                      title="Copiar"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteSnippet(snippet.id)}
                      className="p-1 rounded hover:bg-red-400/10 theme-text-muted hover:text-red-400 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
